import { FormikContextType } from 'formik'

import sentryService from '@/lib/services/sentryService'

export function scrollToFirstError<Values>(
  helpers?: FormikContextType<Values>,
) {
  // Try finding the first error message
  const firstErrorMessage =
    document.querySelector<HTMLElement>('[data-field-error]')

  // If we can't find it, log a warning and don't do anything else
  if (!firstErrorMessage) {
    console.warn('Failed to find error field', helpers?.errors)

    sentryService.captureMessage({
      message: 'Failed to find error field',
      messageId: 'failed-to-find-error-field',
    })

    return
  }

  const inputId = firstErrorMessage.dataset.fieldError

  // Try finding the invalid form field and autofocus it.
  const fieldElement = document.querySelector<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >(`[data-field="${inputId}"]`)
  const isFound =
    fieldElement &&
    (fieldElement instanceof HTMLInputElement ||
      fieldElement instanceof HTMLTextAreaElement ||
      fieldElement instanceof HTMLSelectElement)
  if (isFound) {
    fieldElement.focus()
    return
  }

  // We couldn't find the form field. Let's try to find the corresponding
  // field label and scroll to that.
  const label = document.querySelector(`label[for="${inputId}"]`)
  if (label) {
    label.scrollIntoView()
    return
  }

  // Failed to find the label. Let's scroll to the error message instead
  firstErrorMessage.scrollIntoView()
}

export function inputId(name: string) {
  return `field_${name}`
}

/**
 * The first message inside a Formik error, which is a plain string for simple
 * fields but a nested structure of them for array and object fields. Anything
 * showing a single message needs flattening: without it an error on an item
 * (e.g. `meetingFormats[1]`) leaves the form reporting errors it never shows.
 */
export function getFormikErrorMessage(error: unknown): string | undefined {
  if (typeof error === 'string') {
    return error
  }

  const nestedErrors = Array.isArray(error)
    ? error
    : typeof error === 'object' && error !== null
      ? Object.values(error)
      : []

  for (const nestedError of nestedErrors) {
    const message = getFormikErrorMessage(nestedError)

    if (message) {
      return message
    }
  }

  return undefined
}
